import React, { useState, useEffect } from 'react';
import {
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { authApi } from '../../api/authApi';
import loginBackground from '../../assets/login_background.webp';
import './login.css';

const Brand = ({ size = 'sm' }) => (
  <div className="login-brand">
    <div className="login-brand-mark">T</div>
    <div className="login-brand-name">Transcript{size === 'lg' ? ' · Diplo AI' : ''}</div>
  </div>
);

export const LoginForm = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('access_token');
    const email = params.get('email');

    if (token && email) {
      localStorage.setItem('diploAccessKey', token);
      localStorage.setItem('diploAccessUser', email);
      window.location.href = '/';
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (loginError) setLoginError('');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is not valid';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    return errors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setLoginError('');

    try {
      const response = await authApi.login({
        email: formData.email,
        password: formData.password,
      });

      if (response.access_token) {
        localStorage.setItem('diploAccessKey', response.access_token);
        localStorage.setItem('diploAccessUser', formData.email);
        window.location.href = '/';
      } else {
        setLoginError('Login successful but no access token received');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(error.message || 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSSOLogin = () => {
    setSsoLoading(true);
    setLoginError('');

    authApi
      .getSSORedirect()
      .then((response) => {
        window.location.href = response;
      })
      .catch((error) => {
        console.error('SSO redirect error:', error);
        setLoginError('SSO login failed. Please try again later.');
        setSsoLoading(false);
      });
  };

  return (
    <div className="login-page">
      <aside
        className="login-aside"
        style={{ '--login-bg': `url(${loginBackground})` }}
      >
        <div className="login-aside-bg" aria-hidden="true" />
        <div className="login-aside-overlay" aria-hidden="true" />

        <Brand size="lg" />

        <div className="login-aside-content">
          <span className="login-aside-eyebrow">
            <span className="login-aside-eyebrow-dot" aria-hidden="true" />
            AI Transcript Platform
          </span>
          <h2 className="login-aside-title">
            Transcripts, reports and insights — in one quiet place.
          </h2>
          <p className="login-aside-sub">
            Upload audio or video and let Diplo AI turn it into searchable,
            speaker‑attributed transcripts and structured session reports.
          </p>

          <div className="login-aside-features">
            <div className="login-aside-feature">
              <BoltIcon />
              <span>Automatic transcription with multiple providers</span>
            </div>
            <div className="login-aside-feature">
              <SparklesIcon />
              <span>Speaker diarization and editable transcript blocks</span>
            </div>
            <div className="login-aside-feature">
              <ShieldCheckIcon />
              <span>Secure SSO and access controlled by your organization</span>
            </div>
          </div>
        </div>

        <div className="login-aside-foot">
          © {new Date().getFullYear()} DiploFoundation
        </div>
      </aside>

      <main className="login-main">
        <div className="login-card">
          <div className="login-mobile-brand">
            <Brand />
          </div>

          <h1 className="login-title">Sign in</h1>
          <p className="login-sub">
            Welcome back. Use your email and password, or continue with Diplo SSO.
          </p>

          {loginError && (
            <div className="login-alert" role="alert">
              <ExclamationTriangleIcon />
              <span>{loginError}</span>
            </div>
          )}

          <form onSubmit={handleFormSubmit} className="login-form" noValidate>
            <div className="login-field">
              <label htmlFor="email" className="login-label">
                Email
              </label>
              <div className="login-input-wrap">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`login-input ${formErrors.email ? 'has-error' : ''}`}
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  autoComplete="email"
                />
              </div>
              {formErrors.email && (
                <p className="login-field-error">{formErrors.email}</p>
              )}
            </div>

            <div className="login-field">
              <div className="login-field-row">
                <label htmlFor="password" className="login-label">
                  Password
                </label>
                <a href="#" className="login-link">Forgot?</a>
              </div>
              <div className="login-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`login-input has-suffix ${formErrors.password ? 'has-error' : ''}`}
                  placeholder="Enter your password"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-input-suffix"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={isSubmitting}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                </button>
              </div>
              {formErrors.password && (
                <p className="login-field-error">{formErrors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="login-btn login-btn-primary"
            >
              {isSubmitting ? (
                <>
                  <span className="login-spinner" aria-hidden="true" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <div className="login-divider">or</div>

          <button
            type="button"
            onClick={handleSSOLogin}
            disabled={ssoLoading}
            className="login-btn login-btn-secondary"
          >
            {ssoLoading ? (
              <>
                <span className="login-spinner" aria-hidden="true" />
                Connecting to SSO...
              </>
            ) : (
              <>
                <ShieldCheckIcon style={{ width: 16, height: 16 }} />
                Continue with Diplo SSO
              </>
            )}
          </button>

          <div className="login-foot">
            Don't have an account?{' '}
            <a href="#">Contact your administrator</a>
          </div>
        </div>
      </main>
    </div>
  );
};
